// Localised labels for Revit BuiltInCategory enum names.
//
// The catalog stores the language-neutral enum ("OST_Doors") so a German and a Chilean user filter
// to the same thing; the label is looked up here per UI locale. This is intentionally a small,
// curated map rather than a generated dump of all ~1000 categories — the catalog only ever shows
// the ones families are actually filed under, and a missing entry degrades to a readable fallback
// derived from the enum name.

import type { SupportedLocale } from "@/i18n/index.svelte";

// One entry per category, per shipped UI locale. A missing locale on an entry (or a category not
// listed at all) degrades to English, then to a readable form of the enum name — see categoryLabel.
type LabelMap = Record<string, Partial<Record<SupportedLocale, string>>>;

const LABELS: LabelMap = {
  OST_StructuralFraming: { en: "Structural Framing", de: "Tragwerk", es: "Estructura", fr: "Ossature", ja: "構造フレーム", ru: "Несущий каркас", "zh-Hans": "结构框架", "zh-TW": "結構構架" },
  OST_Doors: { en: "Doors", de: "Türen", es: "Puertas", fr: "Portes", ja: "ドア", ru: "Двери", "zh-Hans": "门", "zh-TW": "門" },
  OST_Windows: { en: "Windows", de: "Fenster", es: "Ventanas", fr: "Fenêtres", ja: "窓", ru: "Окна", "zh-Hans": "窗", "zh-TW": "窗" },
  OST_Furniture: { en: "Furniture", de: "Möbel", es: "Mobiliario", fr: "Mobilier", ja: "家具", ru: "Мебель", "zh-Hans": "家具", "zh-TW": "家具" },
  OST_GenericModel: { en: "Generic Models", de: "Allgemeines Modell", es: "Modelos genéricos", fr: "Modèles génériques", ja: "汎用モデル", ru: "Обобщённые модели", "zh-Hans": "常规模型", "zh-TW": "一般模型" },
  OST_Casework: { en: "Casework", de: "Einbaumöbel", es: "Muebles fijos", fr: "Meubles", ja: "造作家具", ru: "Встроенная мебель", "zh-Hans": "橱柜", "zh-TW": "櫥櫃" },
  OST_LightingFixtures: { en: "Lighting Fixtures", de: "Leuchten", es: "Luminarias", fr: "Luminaires", ja: "照明器具", ru: "Светильники", "zh-Hans": "照明设备", "zh-TW": "照明設備" },
  OST_PlumbingFixtures: { en: "Plumbing Fixtures", de: "Sanitärobjekte", es: "Artefactos sanitarios", fr: "Appareils sanitaires", ja: "衛生器具", ru: "Сантехника", "zh-Hans": "卫浴装置", "zh-TW": "衛浴設備" },
  OST_MechanicalEquipment: { en: "Mechanical Equipment", de: "Mechanische Ausrüstung", es: "Equipos mecánicos", fr: "Équipements mécaniques", ja: "機械設備", ru: "Механическое оборудование", "zh-Hans": "机械设备", "zh-TW": "機械設備" },
  OST_ElectricalEquipment: { en: "Electrical Equipment", de: "Elektrische Ausrüstung", es: "Equipos eléctricos", fr: "Équipements électriques", ja: "電気設備", ru: "Электрооборудование", "zh-Hans": "电气设备", "zh-TW": "電氣設備" },
  OST_StructuralColumns: { en: "Structural Columns", de: "Stützen", es: "Pilares estructurales", fr: "Poteaux porteurs", ja: "構造柱", ru: "Несущие колонны", "zh-Hans": "结构柱", "zh-TW": "結構柱" },
  OST_StructuralFoundation: { en: "Structural Foundations", de: "Gründung", es: "Fundaciones", fr: "Fondations", ja: "構造基礎", ru: "Фундаменты", "zh-Hans": "结构基础", "zh-TW": "結構基礎" },
};

/** The curated category enums, for the publish form's category picker. */
export const KNOWN_CATEGORIES: readonly string[] = Object.keys(LABELS);

/** Family authoring languages offered in the publish form (an open set; these are the common ones). */
export const AUTHORING_LANGUAGES: readonly string[] = ["en", "es", "de", "ru", "fr", "it", "zh"];

/**
 * Level of Development, per the BIMForum LOD Specification — mirrors Apibim.Contracts.LodLevels
 * on the server, which is the actual source of truth (the server rejects anything not in this set
 * regardless of what the form offers). A closed set, not an open one like AUTHORING_LANGUAGES: LOD
 * is a classification with a fixed vocabulary, the same reason RevitCategory is a select and not a
 * free-text field.
 */
export const LOD_LEVELS: readonly number[] = [100, 200, 300, 350, 400, 500];

/**
 * A human label for a category enum in the given locale. Falls back to the enum's readable core
 * ("OST_FooBar" → "Foo Bar") so an uncatalogued category is still legible rather than raw.
 */
export function categoryLabel(enumName: string, locale: SupportedLocale): string {
  const entry = LABELS[enumName];
  if (entry) return entry[locale] ?? entry.en ?? fallbackLabel(enumName);

  return fallbackLabel(enumName);
}

function fallbackLabel(enumName: string): string {
  return enumName
    .replace(/^OST_/, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2");
}

/**
 * A human label for a tree node.
 *
 * Sections and categories arrive as language-neutral keys and are translated; a subcategory is text
 * its author typed and is shown exactly as written. Translating it would mean inventing a
 * translation for a word we were handed.
 */
export function nodeLabel(
  level: "section" | "category" | "subcategory",
  key: string,
  locale: SupportedLocale,
  t: (key: string) => string,
): string {
  if (level === "category") return categoryLabel(key, locale);
  if (level === "section") return t(`sections.${key}`);
  return key;
}

/** Human label for an authoring-language tag. These are family languages (open set), not UI locales. */
export function languageLabel(tag: string | null | undefined): string {
  if (!tag) return "—";
  const names: Record<string, string> = {
    en: "English",
    de: "Deutsch",
    ru: "Русский",
    es: "Español",
    fr: "Français",
    it: "Italiano",
    "zh": "中文",
  };
  return names[tag] ?? tag;
}
